/**
 * Studio Creator Tabs Types
 *
 * Wraps the studio-side creator-tab contract (`/api/studio/tabs`, Sanctum,
 * bound by UUID). Studio list/get responses include inactive tabs and
 * draft/hidden videos; `videos_count` is populated on the list response.
 *
 * Tab reorder and intra-tab video reorder both require TOTAL COVERAGE —
 * the `*_uuids` payload must list every sibling uuid; partial lists are
 * rejected with 422.
 *
 * `type` discriminates row kinds (contract: creator-tabs-system-markers v3):
 * `'manual'` is a creator-managed tab with pinned videos; the system-marker
 * values (`videos` / `shorts` / `posts` / `education` / `series`) are ordering
 * markers for the default profile sections — no pinned videos, non-editable,
 * lazily materialized by the backend on first reorder. Future algorithmic
 * types (`featured` / `recent`) will extend the union deliberately.
 */

import type { Video } from './video';

export const SYSTEM_TAB_TYPES = ['videos', 'shorts', 'posts', 'education', 'series'] as const;

export type SystemTabType = (typeof SYSTEM_TAB_TYPES)[number];

export type CreatorTabType = 'manual' | SystemTabType;

export interface CreatorTab {
  uuid: string;
  type: 'manual';
  /** Manual rows may carry an explicit `false`; system markers are `SystemMarkerTab`. */
  system_marker?: false;
  title: string;
  description: string | null;
  color: string | null;
  icon: string | null;
  active: boolean;
  display_order: number;
  videos_count: number;
  /**
   * Up to 4 thumbnail URLs for the pinned videos, for the manager row preview
   * stack. Backend-optional (pending BE); the stack is hidden entirely when the
   * field is absent rather than showing placeholders.
   */
  preview_thumbnails?: string[];
  created_at: string;
  updated_at: string;
}

/**
 * Persisted system-marker row (`system_marker: true`). Exists only after the
 * creator's first reorder materializes it. Non-editable, non-deletable, no
 * pinned videos — `videos_count` is null/omitted so a misleading `0` never
 * renders; section counts come from the profile payload.
 */
export interface SystemMarkerTab {
  uuid: string;
  type: SystemTabType;
  /** Informational server label — FE renders its own hardcoded labels. */
  title: string;
  description: null;
  color: null;
  icon: null;
  active: boolean;
  display_order: number;
  system_marker: true;
  videos_count?: null;
  created_at: string;
  updated_at: string;
}

/** A row of the studio tabs list response: mixed manual tabs and system markers, server-sorted. */
export type CreatorTabRow = CreatorTab | SystemMarkerTab;

/** `GET /studio/tabs/{uuid}` — single tab with its pinned videos (drafts + hidden included). */
export interface CreatorTabDetail extends CreatorTab {
  videos: Video[];
}

export interface CreateTabPayload {
  title: string;
  description?: string;
  /** Only `'manual'` is creatable — system markers exist solely via reorder materialization (BE rejects the rest). */
  type?: 'manual';
  /** `#RRGGBB`. */
  color?: string;
  /** Icon identifier, max 50 chars. */
  icon?: string;
  active?: boolean;
}

export type UpdateTabPayload = Partial<CreateTabPayload>;

/**
 * @deprecated Legacy body for `PATCH /studio/tabs/reorder` (`tab_uuids`) — cannot
 * reference system markers. BE keeps it for a 60-day sunset; new code sends
 * `ReorderTabsItemsPayload`. Remove with the Step-3 client swap.
 */
export interface ReorderTabsPayload {
  tab_uuids: string[];
}

/**
 * One entry of the v3 reorder body: an existing row by uuid (manual or
 * materialized marker), or a not-yet-materialized system marker by type.
 */
export type ReorderTabItem = { uuid: string } | { type_marker: SystemTabType };

/**
 * v3 body for `PATCH /studio/tabs/reorder`. Total coverage: every manual uuid
 * and every materialized system uuid must be present (422 otherwise);
 * `type_marker` entries are the only allowed not-yet-persisted references and
 * are materialized in one transaction on save.
 */
export interface ReorderTabsItemsPayload {
  items: ReorderTabItem[];
}

/** Body for `PATCH /studio/tabs/{uuid}/videos/reorder`. Must list every video uuid in the tab — partial lists rejected with 422. */
export interface ReorderTabVideosPayload {
  video_uuids: string[];
}

/** Response for `POST /studio/tabs/{tabUuid}/videos/{videoUuid}`. `position` absent when `already_pinned` is true. */
export interface PinVideoResponse {
  already_pinned: boolean;
  position?: number;
}

/** Response for `DELETE /studio/tabs/{uuid}`. */
export interface DeleteTabResponse {
  deleted_uuid: string;
  videos_unpinned: number;
}
